'use client'

import Link from 'next/link'
import { useParams } from 'next/navigation'
import { defaultLocale, isLocale, localeHref } from '@/app/lib/i18n'
import { getMessages } from '@/app/messages'

/**
 * Converted to Tailwind as a like-for-like refactor - same markup, same links,
 * same computed styles (see Hero.tsx for the rationale). `data-farf-site` opts
 * this subtree into the scoped reset in globals.css.
 *
 * The link hover colour is now a real CSS `hover:` rule rather than the inline
 * `transition` that had nothing to trigger it - previously these links declared
 * a colour transition but never changed colour on hover.
 *
 * Rendered once from the root layout (outside app/[locale]), so it has no
 * `locale` prop to read - it pulls the current route's locale segment
 * client-side instead, the same way ConditionalFooter already reads
 * `usePathname()` for the same reason.
 */
export default function Footer() {
  const params = useParams<{ locale?: string }>()
  const locale = isLocale(params.locale ?? '') ? (params.locale as typeof defaultLocale) : defaultLocale
  const t = getMessages(locale).nav

  const serviceLinks = [
    { name: t.homeLabel, href: localeHref(locale, '/') },
    { name: t.howItWorksLabel, href: localeHref(locale, '/how-it-works') },
    { name: t.faqLabel, href: localeHref(locale, '/faq') },
    { name: t.downloadLabel, href: '/download' },
  ]

  // Short nav labels (t.blogLinks), not each post's full SEO title - a
  // footer list needs "Twitter Backup", not "Twitter Backup - How to
  // Backup Your Twitter Account & Recover Deleted Tweets".
  const resourceLinks = [
    { name: t.blogLabel, href: localeHref(locale, '/blog') },
    ...t.blogLinks.map((link) => ({ name: link.name, href: localeHref(locale, link.href) })),
  ]

  const footerLinks: Record<string, { name: string; href: string }[]> = {
    [t.serviceHeading]: serviceLinks,
    [t.resourcesHeading]: resourceLinks,
  }

  return (
    <footer data-farf-site className="mt-20 bg-black px-5 pb-10 pt-[60px] text-white">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-10 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-10">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="flex flex-col gap-4">
              <h3 className="mb-2 text-lg font-semibold">{category}</h3>
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-base text-[#cccccc] no-underline transition-colors duration-200 hover:text-white"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          ))}
        </div>
        <div className="border-t border-[#333333] pt-10 text-center text-sm text-[#999999]">
          <p>{t.copyright}</p>
          <p className="mt-3">
            {t.contactLabel}:{' '}
            <a
              href="mailto:contact@seedeletedtweets.com"
              className="text-[#cccccc] no-underline transition-colors duration-200 hover:text-white"
            >
              contact@seedeletedtweets.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  )
}
